// Generated by GitHub Copilot
import { useState } from "react";
import { motion } from "framer-motion";
import LoadingFallback from "./LoadingFallback";

const SKILLS = [
  { name: "Python", level: 92 },
  { name: "React", level: 84 },
  { name: "MLOps", level: 88 },
  { name: "AWS", level: 80 },
  { name: "LLMs", level: 90 },
];

const CERTIFICATION_PREVIEW_COUNT = 8;

const SKILL_GROUP_RULES = [
  {
    title: "AI and ML",
    matcher:
      /(machine learning|artificial intelligence|\bai\b|\bml\b|llm|nlp|prompt|computer vision|deep learning|rag|mlops|yolo|emotion|speech)/i,
  },
  {
    title: "Frontend and Web",
    matcher: /(react|next\.?js|javascript|typescript|mern|web|frontend|ui|ux|micro-frontend)/i,
  },
  {
    title: "Cloud and Platform",
    matcher: /(aws|gcp|azure|docker|kubernetes|gitlab|github|copilot|vertex|api)/i,
  },
  {
    title: "Data and Foundations",
    matcher: /(python|sql|oracle|dbms|data structures|operating systems|object-oriented|statistical|analysis|oop)/i,
  },
];

/**
 * Group skill labels into high-signal recruiter-friendly categories.
 *
 * @param {Array<string>} skillLabels Flat list of skill names.
 * @returns {Array<{title: string, items: string[]}>} Grouped skill entries.
 */
function buildSkillGroups(skillLabels) {
  const uniqueSkillLabels = Array.from(
    new Set(
      (Array.isArray(skillLabels) ? skillLabels : [])
        .map((label) => (typeof label === "string" ? label.trim() : ""))
        .filter(Boolean)
    )
  );

  if (!uniqueSkillLabels.length) {
    return [];
  }

  const groupedItems = SKILL_GROUP_RULES.map((rule) => ({
    title: rule.title,
    items: [],
  }));

  for (const label of uniqueSkillLabels) {
    const ruleIndex = SKILL_GROUP_RULES.findIndex((rule) => rule.matcher.test(label));

    if (ruleIndex >= 0) {
      groupedItems[ruleIndex].items.push(label);
    }
  }

  return groupedItems.filter((group) => group.items.length);
}

/**
 * Render profile background with skill bars, language proficiency, and experience highlights.
 *
 * @param {{config: Record<string, any>, profile: Record<string, any>, loading?: boolean}} props Component props.
 * @returns {JSX.Element} About section.
 */
function About({ config, profile, loading = false }) {
  const [showAllCertifications, setShowAllCertifications] = useState(false);

  const skills = profile?.topSkills?.length ? profile.topSkills : config?.skills?.length ? config.skills : SKILLS;
  const additionalSkills = Array.isArray(profile?.skills) ? profile.skills : [];
  const skillGroups = buildSkillGroups([
    ...skills
      .map((skill) => (typeof skill === "string" ? skill : skill?.name))
      .filter(Boolean),
    ...additionalSkills,
  ]);
  const languages = profile?.languages || [];
  const experience = profile?.experience || [];
  const certifications = profile?.certifications || [];
  const visibleCertifications = showAllCertifications
    ? certifications
    : certifications.slice(0, CERTIFICATION_PREVIEW_COUNT);
  const volunteerExperience = profile?.volunteerExperience || [];
  const hackathons = profile?.hackathons || [];
  const linkedinProjects = profile?.linkedinProjects || [];
  const testScores = profile?.testScores || [];
  const interests = profile?.identity?.interests || [];
  const linkedinUrl = profile?.contact?.linkedin || "https://www.linkedin.com/in/ramprakashdhulipudi/";
  const summary = config?.summary || profile?.identity?.professionalSummary || profile?.identity?.summary;

  return (
    <section className="py-16" id="about">
      <motion.h2
        className="text-3xl font-bold"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ amount: 0.3, once: true }}
        transition={{ duration: 0.55, ease: "easeOut" }}
      >
        {config?.title}
      </motion.h2>
      <motion.p
        className="mt-2 max-w-3xl text-[var(--text-secondary)]"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ amount: 0.3, once: true }}
        transition={{ duration: 0.55, delay: 0.1, ease: "easeOut" }}
      >
        {summary}
      </motion.p>

      {loading ? (
        <div className="mt-8">
          <LoadingFallback
            label="Loading about content"
            headingWidth={240}
            subheadingWidth={180}
            cardCount={4}
            rowCount={5}
          />
        </div>
      ) : (
        <>
          <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr,0.8fr]">
            <motion.div
              className="glass-card space-y-4 rounded-3xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2, once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <h3 className="text-xl font-semibold">Top Skills</h3>
              {skills.map((skill) => (
                <div key={skill.name}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span>{skill.name}</span>
                    <span>{skill.level}%</span>
                  </div>
                  <div
                    className="h-3 overflow-hidden rounded-full"
                    style={{ background: "color-mix(in srgb, var(--glass-surface-strong) 70%, transparent 30%)" }}
                  >
                    <div
                      className="h-full rounded-full bg-[linear-gradient(90deg,rgba(var(--accent-secondary-rgb),0.9),rgba(var(--accent-rgb),0.96))] transition-all duration-700 ease-out"
                      style={{ width: `${skill.level}%` }}
                      aria-label={`${skill.name} proficiency ${skill.level} percent`}
                    />
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.aside
              className="glass-card rounded-3xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2, once: true }}
              transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            >
              <h3 className="text-xl font-semibold">Languages</h3>
              <ul className="mt-4 space-y-2">
                {languages.map((language) => (
                  <li key={language.name} className="glass-card-soft rounded-xl px-3 py-2 text-sm">
                    <span className="font-semibold">{language.name}</span>
                    <span className="ml-2 text-[var(--text-secondary)]">{language.proficiency}</span>
                  </li>
                ))}
              </ul>
            </motion.aside>
          </div>

          {skillGroups.length ? (
            <motion.div
              className="glass-card mt-6 rounded-3xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2, once: true }}
              transition={{ duration: 0.6, delay: 0.11, ease: "easeOut" }}
            >
              <h3 className="text-xl font-semibold">Skill Categories</h3>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {skillGroups.map((group) => (
                  <article key={group.title} className="glass-card-soft rounded-2xl p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--accent-rgb),0.95)]">
                      {group.title}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {group.items.map((item) => (
                        <span key={`${group.title}-${item}`} className="glass-chip rounded-full px-3 py-1 text-xs text-[var(--text-primary)]">
                          {item}
                        </span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </motion.div>
          ) : null}

          {additionalSkills.length ? (
            <motion.div
              className="glass-card mt-6 rounded-3xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2, once: true }}
              transition={{ duration: 0.6, delay: 0.12, ease: "easeOut" }}
            >
              <h3 className="text-xl font-semibold">All Skills</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {additionalSkills.map((skillName) => (
                  <span key={skillName} className="glass-chip rounded-full px-3 py-1 text-xs text-[var(--text-primary)]">
                    {skillName}
                  </span>
                ))}
              </div>
            </motion.div>
          ) : null}

          {interests.length ? (
            <motion.div
              className="glass-card mt-6 rounded-3xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2, once: true }}
              transition={{ duration: 0.6, delay: 0.13, ease: "easeOut" }}
            >
              <h3 className="text-xl font-semibold">Interests</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {interests.map((interest) => (
                  <span key={interest} className="glass-chip rounded-full px-3 py-1 text-xs text-[var(--text-primary)]">
                    {interest}
                  </span>
                ))}
              </div>
            </motion.div>
          ) : null}

          <motion.div
            className="glass-card mt-6 rounded-3xl p-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2, once: true }}
            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
          >
            <h3 className="text-xl font-semibold">Experience Highlights</h3>
            {experience.length ? (
              <ol className="mt-4 space-y-4">
                {experience.map((role) => (
                  <li key={`${role.company}-${role.role}`} className="glass-card-soft rounded-2xl p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-base font-semibold">{role.role}</p>
                      <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-secondary)]">{role.period}</p>
                    </div>
                    <p className="mt-1 text-sm text-[rgba(var(--accent-rgb),0.95)]">{role.company}</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[var(--text-secondary)]">
                      {(role.highlights || []).map((highlight) => (
                        <li key={highlight}>{highlight}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-4 text-sm text-[var(--text-secondary)]">
                Experience entries are currently unavailable in the synced profile payload.
              </p>
            )}
          </motion.div>

          <motion.div
            className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2, once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            <article className="glass-card rounded-3xl p-6">
              <h3 className="text-xl font-semibold">Certifications</h3>
              {certifications.length ? (
                <>
                  <ul className="mt-4 space-y-2">
                    {visibleCertifications.map((certification) => (
                      <li key={certification} className="glass-card-soft rounded-xl px-3 py-2 text-sm">
                        {certification}
                      </li>
                    ))}
                  </ul>
                  {certifications.length > CERTIFICATION_PREVIEW_COUNT ? (
                    <button
                      type="button"
                      className="mt-4 text-sm font-semibold text-[rgba(var(--accent-rgb),0.95)] hover:underline"
                      onClick={() => setShowAllCertifications((current) => !current)}
                      aria-expanded={showAllCertifications}
                    >
                      {showAllCertifications ? "Show Less" : `Show More (${certifications.length - CERTIFICATION_PREVIEW_COUNT})`}
                    </button>
                  ) : null}
                </>
              ) : (
                <p className="mt-4 text-sm text-[var(--text-secondary)]">
                  Certification entries can be synchronized from LinkedIn profile details.
                </p>
              )}
            </article>

            <article className="glass-card rounded-3xl p-6">
              <h3 className="text-xl font-semibold">Volunteer</h3>
              {volunteerExperience.length ? (
                <ul className="mt-4 space-y-3">
                  {volunteerExperience.map((item) => (
                    <li key={`${item.organization}-${item.role}`} className="glass-card-soft rounded-xl px-3 py-3 text-sm">
                      <p className="font-semibold">{item.role}</p>
                      <p className="text-[var(--text-secondary)]">{item.organization}</p>
                      {item.period ? <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[var(--text-secondary)]">{item.period}</p> : null}
                      {Array.isArray(item.highlights) && item.highlights.length ? (
                        <ul className="mt-2 list-disc space-y-1 pl-4 text-[var(--text-secondary)]">
                          {item.highlights.map((highlight) => (
                            <li key={highlight}>{highlight}</li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-sm text-[var(--text-secondary)]">
                  Volunteer entries are currently not available in the synced dataset.
                </p>
              )}
            </article>

            <article className="glass-card rounded-3xl p-6">
              <h3 className="text-xl font-semibold">Hackathons</h3>
              {hackathons.length ? (
                <ul className="mt-4 space-y-3">
                  {hackathons.map((item) => (
                    <li key={`${item.name}-${item.year || item.period || "event"}`} className="glass-card-soft rounded-xl px-3 py-3 text-sm">
                      <p className="font-semibold">{item.name}</p>
                      {item.achievement ? <p className="mt-1 text-[var(--text-secondary)]">{item.achievement}</p> : null}
                      {item.year || item.period ? (
                        <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                          {item.year || item.period}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-sm text-[var(--text-secondary)]">
                  Hackathon entries are currently not available in the synced dataset.
                </p>
              )}
            </article>

            <article className="glass-card rounded-3xl p-6">
              <h3 className="text-xl font-semibold">Test Scores</h3>
              {testScores.length ? (
                <ul className="mt-4 space-y-2">
                  {testScores.map((item) => (
                    <li key={`${item.name}-${item.date || "score"}`} className="glass-card-soft rounded-xl px-3 py-2 text-sm">
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-[var(--text-secondary)]">Score: {item.score}</p>
                      {item.date ? <p className="text-xs uppercase tracking-[0.14em] text-[var(--text-secondary)]">{item.date}</p> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-sm text-[var(--text-secondary)]">No test score data available.</p>
              )}
            </article>
          </motion.div>

          <motion.div
            className="glass-card mt-6 rounded-3xl p-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2, once: true }}
            transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-xl font-semibold">LinkedIn Projects</h3>
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold text-[rgba(var(--accent-rgb),0.95)] hover:underline"
              >
                View full LinkedIn profile
              </a>
            </div>

            {linkedinProjects.length ? (
              <ul className="mt-4 space-y-3">
                {linkedinProjects.map((project) => (
                  <li key={`${project.title}-${project.organization || "project"}`} className="glass-card-soft rounded-2xl p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-semibold">{project.title}</p>
                      {project.period ? (
                        <p className="text-xs uppercase tracking-[0.14em] text-[var(--text-secondary)]">{project.period}</p>
                      ) : null}
                    </div>
                    {project.organization ? (
                      <p className="mt-1 text-sm text-[rgba(var(--accent-rgb),0.95)]">{project.organization}</p>
                    ) : null}
                    {project.description ? (
                      <p className="mt-2 text-sm text-[var(--text-secondary)]">{project.description}</p>
                    ) : null}
                    {project.link ? (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block text-sm font-semibold text-[rgba(var(--accent-rgb),0.95)] hover:underline"
                      >
                        Open project
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 text-sm text-[var(--text-secondary)]">
                LinkedIn-only project entries can be synchronized into this profile section.
              </p>
            )}
          </motion.div>
        </>
      )}
    </section>
  );
}

export default About;
